import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AdminListPage } from '@/features/admin/components/AdminListPage'
import { AdminTestimonialRow } from '../components/AdminTestimonialRow'
import { AdminPhotoUploadDialog } from '../components/AdminPhotoUploadDialog'
import { AdminAddTestimonialDialog } from '../components/AdminAddTestimonialDialog'
import { useAdminTestimonials } from '../hooks/useAdminTestimonials'
import { testimonialService } from '../services/testimonial.service'
import type { TestimonialWithId } from '../services/testimonial.service'

export function AdminTestimonialsPage() {
  const { pending, approved, rejected, loading, refetch } = useAdminTestimonials()

  const [addOpen, setAddOpen] = useState(false)
  const [photoTarget, setPhotoTarget] = useState<TestimonialWithId | null>(null)

  async function handleApprove(id: string) {
    await testimonialService.approve(id)
    refetch()
  }

  async function handleReject(id: string) {
    await testimonialService.reject(id)
    refetch()
  }

  async function handleDelete(id: string) {
    await testimonialService.deleteTestimonial(id)
    refetch()
  }

  async function handleMoveUp(testimonial: TestimonialWithId, list: TestimonialWithId[]) {
    const index = list.findIndex((t) => t.id === testimonial.id)
    if (index <= 0) return
    const prev = list[index - 1]!
    await Promise.all([
      testimonialService.updateOrder(testimonial.id, prev.order),
      testimonialService.updateOrder(prev.id, testimonial.order),
    ])
    refetch()
  }

  async function handleMoveDown(testimonial: TestimonialWithId, list: TestimonialWithId[]) {
    const index = list.findIndex((t) => t.id === testimonial.id)
    if (index < 0 || index >= list.length - 1) return
    const next = list[index + 1]!
    await Promise.all([
      testimonialService.updateOrder(testimonial.id, next.order),
      testimonialService.updateOrder(next.id, testimonial.order),
    ])
    refetch()
  }

  function EmptyRow({ message }: { message: string }) {
    return (
      <TableRow>
        <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
          {message}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      <AdminListPage
        title="Testimonials"
        action={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add Manually
          </Button>
        }
        loading={loading}
        skeletonRows={4}
      >
        <Tabs defaultValue="pending" className="w-full">
          <div className="border-b px-4 pt-4">
            <TabsList variant="line">
              <TabsTrigger value="pending">
                Pending
                {pending.length > 0 && (
                  <Badge variant="default" className="ml-1.5 text-xs">
                    {pending.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="approved">
                Approved
                {approved.length > 0 && (
                  <Badge variant="secondary" className="ml-1.5 text-xs">
                    {approved.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="rejected">
                Rejected
                {rejected.length > 0 && (
                  <Badge variant="outline" className="ml-1.5 text-xs">
                    {rejected.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* ── Pending ─────────────────────────────────────────────────── */}
          <TabsContent value="pending">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Content</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.length === 0 ? (
                  <EmptyRow message="No pending testimonials." />
                ) : (
                  pending.map((t) => (
                    <AdminTestimonialRow
                      key={t.id}
                      testimonial={t}
                      onApprove={() => void handleApprove(t.id)}
                      onReject={() => void handleReject(t.id)}
                      onAddPhoto={() => setPhotoTarget(t)}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TabsContent>

          {/* ── Approved ─────────────────────────────────────────────────── */}
          <TabsContent value="approved">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Content</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {approved.length === 0 ? (
                  <EmptyRow message="No approved testimonials yet." />
                ) : (
                  approved.map((t, index) => (
                    <AdminTestimonialRow
                      key={t.id}
                      testimonial={t}
                      onReject={() => void handleReject(t.id)}
                      onAddPhoto={() => setPhotoTarget(t)}
                      onMoveUp={() => void handleMoveUp(t, approved)}
                      onMoveDown={() => void handleMoveDown(t, approved)}
                      isFirst={index === 0}
                      isLast={index === approved.length - 1}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TabsContent>

          {/* ── Rejected ─────────────────────────────────────────────────── */}
          <TabsContent value="rejected">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Content</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rejected.length === 0 ? (
                  <EmptyRow message="No rejected testimonials." />
                ) : (
                  rejected.map((t) => (
                    <AdminTestimonialRow
                      key={t.id}
                      testimonial={t}
                      onApprove={() => void handleApprove(t.id)}
                      onDelete={() => void handleDelete(t.id)}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </AdminListPage>

      {/* Dialogs */}
      <AdminAddTestimonialDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSuccess={refetch}
      />

      {photoTarget && (
        <AdminPhotoUploadDialog
          testimonialId={photoTarget.id}
          open={photoTarget !== null}
          onOpenChange={(open) => { if (!open) setPhotoTarget(null) }}
          onSuccess={() => {
            setPhotoTarget(null)
            refetch()
          }}
        />
      )}
    </>
  )
}
